import Chip from "../Chip";
import { FLAG_LABELS, VERDICT_LABELS, aiStatusText, verdictColor } from "./aiReviewText";

// Plain-language reason for why the AI did or did not act on its own.
const decisionText = (ai) => {
    const d = ai.decision || {};
    const past = { approve: "approved", reject: "rejected" };
    if (d.action === "approve" || d.action === "reject") return ai.actionStatus === "done" ? `Yes, ${past[d.action]} automatically` : `Tried to ${d.action} automatically`;
    const why = d.why || "";
    if (/auto mode off/.test(why)) return "No, automatic decisions were switched off";
    if (/before auto go-live/.test(why)) return "No, submitted before automatic decisions were switched on";
    if (/not enabled/.test(why)) return "No, automatic decisions were not enabled for this challenge at the time";
    return "No, not a clear full approval at the required confidence. Please review.";
};

// Friendly model name, e.g. "global.anthropic.claude-haiku-4-5-20251001-v1:0" -> "Claude Haiku 4.5".
const modelName = (id = "") => {
    const m = String(id).match(/claude-([a-z]+)-(\d+)-(\d+)/);
    return m ? `Claude ${m[1].charAt(0).toUpperCase()}${m[1].slice(1)} ${m[2]}.${m[3]}` : id;
};

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

// Full AI result card for the submission details page.
const AiReviewPanel = ({ aiReview }) => {
    if (!aiReview || !aiReview.status) {
        return (
            <div className="my-3 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] px-4 py-3">
                <h1 className="text-primary text-xl">AI Review</h1>
                <p className="font-medium text-secondary mt-1">This submission has not been reviewed by AI yet. It is usually done within a minute or two of submission.</p>
            </div>
        );
    }
    if (aiReview.status !== "done") {
        return (
            <div className="my-3 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] px-4 py-3">
                <h1 className="text-primary text-xl">AI Review</h1>
                <p className="font-medium text-secondary mt-1">{aiStatusText(aiReview)}{aiReview.error ? `: ${aiReview.error}` : ""}</p>
            </div>
        );
    }
    const acted = aiReview.actionStatus === "done";
    const result = aiReview.actionResult || {};
    return (
        <div className="my-3 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] px-4 py-3 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-primary text-xl">AI Review</h1>
                <Chip value={aiStatusText(aiReview)} color={verdictColor(aiReview.verdict)} />
                {acted && <span className="text-sm text-secondary font-medium">on {fmt(aiReview.actedAt)}</span>}
            </div>
            <div className="flex flex-wrap gap-6 text-sm font-medium">
                <p>AI verdict: <span className="font-bold">{VERDICT_LABELS[aiReview.verdict]}</span></p>
                <p>Confidence: <span className="font-bold">{aiReview.confidence}%</span></p>
                <p>Reviewed: <span className="font-bold">{fmt(aiReview.reviewedAt)}</span></p>
                {aiReview.decision && <p>Auto decision: <span className="font-bold">{decisionText(aiReview)}</span></p>}
            </div>
            {aiReview.summary && <p className="font-medium">{aiReview.summary}</p>}
            {aiReview.flags && aiReview.flags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {aiReview.flags.map((f) => <Chip key={f} value={FLAG_LABELS[f] || f} color="red" small={true} />)}
                </div>
            )}
            {aiReview.actionStatus === "failed" && (
                <p className="text-red font-medium text-sm">Auto action did not complete ({result.why || result.error || "unknown"}). Please review this submission manually.</p>
            )}
            <p className="text-xs text-gray-400">Model: {modelName(aiReview.model)} · Prompt {aiReview.promptVersion}</p>
        </div>
    );
};

// AI note under a question: verdict, reason, suggested points (computed by the system) and the user-facing message.
export const AiAnswerNote = ({ aiReview, questionId, onApply }) => {
    const done = aiReview && aiReview.status === "done";
    const a = done && (aiReview.answers || []).find((x) => String(x.id) === String(questionId));
    if (!a) return null;
    const sug = ((aiReview.suggestion && aiReview.suggestion.answers) || []).find((x) => String(x.id) === String(questionId));
    const verdict = sug ? sug.verdict : a.verdict;
    return (
        <div className="flex flex-col gap-1 mt-2 text-sm">
            <div className="flex items-start gap-2">
                <Chip value={`AI: ${VERDICT_LABELS[verdict]} ${a.confidence}%`} color={verdictColor(verdict)} small={true} />
                <p className="font-medium text-secondary">{a.reason}</p>
            </div>
            {sug && (
                <div className="flex flex-wrap items-center gap-3 pl-1">
                    <p className="font-medium">
                        Suggested points: <span className="font-bold">{sug.points === null ? "you decide" : `${sug.points} / ${sug.maxPoints}`}</span>
                        <span className="text-secondary"> ({sug.rule}{sug.faceDetail ? `; ${sug.faceDetail}` : ""})</span>
                    </p>
                    {sug.points !== null && onApply && (
                        <button type="button" className="px-2 py-0.5 rounded border border-primary text-primary hover:bg-primary hover:text-white" onClick={() => onApply(sug.points, sug.userMessage || "")}>
                            Use AI points{sug.userMessage ? " + message" : ""}
                        </button>
                    )}
                </div>
            )}
            {sug && sug.userMessage && <p className="pl-1 font-medium">Message for the user: <span className="italic">{sug.userMessage}</span></p>}
        </div>
    );
};

const CARBON_MODE_LABEL = {
    calculated: "AI calculation from the user's answers",
    use_sample: "Sample calculation kept (no clear quantity in the answers)",
    not_measurable: "Not measurable: congratulatory message, 0 points",
};

// AI carbon suggestion on the last approval page. Numbers are computed by the system, not the AI.
export const AiCarbonCard = ({ aiReview, onApply }) => {
    const c = aiReview && aiReview.status === "done" && aiReview.suggestion && aiReview.suggestion.carbon;
    if (!c) return null;
    return (
        <div className="mt-4 border border-primary rounded-md p-4 bg-[#f3fbf5]">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-bold">AI carbon suggestion</h2>
                <button type="button" className="px-3 py-1 rounded bg-primary text-white" onClick={() => onApply(c)}>Use AI calculation</button>
            </div>
            <p className="text-sm mt-1">{CARBON_MODE_LABEL[c.mode] || c.mode}{c.fallback ? ` (${c.fallback})` : ""}</p>
            {c.actionSummary && <p className="text-sm mt-1">What the user did: <span className="font-medium">{c.actionSummary}</span></p>}
            <div className="flex flex-wrap gap-6 text-sm mt-2">
                <p>Exact: <span className="font-bold">{c.kgExact} kg</span></p>
                <p>Rounded: <span className="font-bold">{c.kgRounded} kg</span></p>
                <p>Carbon points: <span className="font-bold">{c.points}</span></p>
            </div>
            <div className="mt-2 text-sm bg-white border border-border rounded p-3" dangerouslySetInnerHTML={{ __html: c.calculationHtml }} />
            <p className="text-xs text-gray-400 mt-1">Pressing the button fills the fields below. Nothing is saved until you press Save.</p>
        </div>
    );
};

export default AiReviewPanel;
